import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lock, Eye, Database, Shield, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Privacy = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const sections = [
    {
      id: 'collect',
      icon: <Database className="w-6 h-6 text-[#00fff5]" />,
      title: 'Information We Collect',
      content: (
        <div className="space-y-4">
          <h4 className="text-[#00fff5] font-rajdhani font-bold">Personal Information:</h4>
          <ul className="list-disc list-inside text-gray-300 ml-4">
            <li>Name and email address (Firebase Authentication)</li>
            <li>Phone number (for UPI verification)</li>
            <li>BGMI and Free Fire in-game IDs</li>
            <li>Match history and performance data</li>
          </ul>
          
          <h4 className="text-[#00fff5] font-rajdhani font-bold mt-4">Payment Information:</h4>
          <ul className="list-disc list-inside text-gray-300 ml-4">
            <li>UPI IDs for withdrawals</li>
            <li>Transaction history for compliance</li>
          </ul>
          <div className="bg-[#00fff510] border border-[#00fff5] rounded-lg p-4">
            <p className="text-[#00fff5]">🔒 We NEVER store full payment details.</p>
          </div>
        </div>
      )
    },
    {
      id: 'use',
      icon: <Eye className="w-6 h-6 text-[#00fff5]" />,
      title: 'How We Use Your Information',
      content: (
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
          <li>Authenticate users and manage accounts</li>
          <li>Process tournament entries and withdrawals</li>
          <li>Verify UPI payments</li>
          <li>Prevent fraud and ensure fair play</li>
          <li>Improve app performance and user experience</li>
          <li>Communicate important updates</li>
        </ul>
      )
    },
    {
      id: 'security',
      icon: <Shield className="w-6 h-6 text-[#00fff5]" />,
      title: 'Data Security',
      content: (
        <div className="space-y-4">
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Data securely stored in Firebase Realtime Database</li>
            <li>Industry-standard encryption for all data</li>
            <li>Regular security audits and access controls</li>
            <li>Delete account feature removes all associated data</li>
          </ul>
          <div className="bg-[#00E67620] border border-[#00E676] rounded-lg p-4 mt-4">
            <p className="text-[#00E676] font-rajdhani font-bold flex items-center gap-2">
              <Lock className="w-5 h-5" />
              256-bit Encryption • SSL Secure • GDPR Compliant
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      <div className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-5"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-5"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
            <Lock className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-rajdhani font-bold text-white mb-4">
            Privacy <span className="text-[#00fff5]">Policy</span>
          </h1>
          <p className="text-xl text-gray-400 font-rajdhani mb-8">Last Updated: March 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="bg-[#1E2338] border border-[#2A2F45] rounded-2xl p-8 mb-8">
          <p className="text-gray-300 text-center font-rajdhani">
            At TheArenaX, we take your privacy seriously. This policy explains how we collect, use, and protect your personal information.
          </p>
        </div>

        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-[#2A2F45] transition"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-[#00fff510] rounded-lg">
                    {section.icon}
                  </div>
                  <span className="text-white font-rajdhani font-bold text-lg">{section.title}</span>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-[#00fff5]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.includes(section.id) && (
                <div className="px-6 pb-6">
                  <div className="h-px bg-[#2A2F45] mb-4"></div>
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-rajdhani font-bold text-white mb-4">Data Protection Officer</h3>
          <p className="text-gray-400 mb-2">SK Prince</p>
          <a 
            href="mailto:support@lastzoneindia.com"
            className="text-[#00fff5] font-rajdhani font-bold hover:underline"
          >
            support@lastzoneindia.com
          </a>
          <p className="text-gray-500 mt-4 text-sm">Response time: Within 48 hours</p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;